import { createMachine, and, not, fromPromise, assign } from 'xstate';
import { store } from '../store/store';
import {
  selectAllBlocksSubmitted,
  selectIsOneBlockSession,
} from '../slices/blocks/blocksSlice';
import {
  loadBlockStudy,
  loadBlockReview,
  loadBlockSelect,
  loadFlashcardStudy,
  loadSessionSummary,
  loadPastBlocks,
  loadHome,
  resetActiveQuestionIndex,
  loadVideoStudy,
} from '../helpers/studyHelpers';
import { selectTodaySession } from '../slices/todaySession/todaySessionSlice';
import { selectCompletedFlashcardsCount } from '../slices/flashcards/flashcardsSlice';

const allBlocksSubmitted = () => selectAllBlocksSubmitted(store.getState());
const flashcardsFinished = () => {
  const todaySession = selectTodaySession(store.getState());
  const completedFlashcardsCount = selectCompletedFlashcardsCount(
    store.getState()
  );
  return completedFlashcardsCount === todaySession?.flashcardCount;
};
const isOneBlockSession = () => selectIsOneBlockSession(store.getState());

// New guard to check if a specific block is submitted
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isBlockSubmitted = (args: { event: any }) => {
  const { event } = args;
  const state = store.getState();
  const blocks = state.blocks.entities;
  const block = blocks[event.blockId];
  return block?.isSubmitted || false;
};

const cameFromPastBlocks = ({ context }: { context: Context }) => {
  return context.returnTo === 'pastBlocks';
};

interface Context {
  returnTo: string | null;
}

const initialContext: Context = {
  returnTo: null,
};

export const studyPhaseMachine = createMachine(
  {
    id: 'study',
    initial: 'home',
    context: initialContext,
    on: {
      START_QBANK: [
        {
          target: '.loadingBlockStudy',
          guard: not(allBlocksSubmitted),
        },
        {
          target: '.loadingBlockReview',
          guard: and([allBlocksSubmitted, isOneBlockSession]),
        },
        {
          target: '.loadingBlockSelect',
          guard: and([allBlocksSubmitted, not(isOneBlockSession)]),
        },
      ],
      PAUSE_BLOCK: [
        {
          target: '.loadingBlockSelect',
          guard: not(isOneBlockSession),
        },
        {
          target: '.loadingHome',
          guard: isOneBlockSession,
        },
      ],
      END_BLOCK: '.blockReview',
      REVIEW_QBANK: {
        target: '.loadingBlockReview',
        actions: assign({
          returnTo: () => null,
        }),
      },
      REVIEW_PAST_QBANK: {
        target: '.loadingBlockReview',
        actions: assign({
          returnTo: () => 'pastBlocks',
        }),
      },
      END_QBANK_REVIEW: [
        {
          target: '.loadingPastBlocks',
          guard: cameFromPastBlocks,
          actions: [assign({ returnTo: () => null })],
        },
        {
          target: '.loadingBlockSelect',
          guard: not(allBlocksSubmitted),
          actions: [assign({ returnTo: () => null }), resetActiveQuestionIndex],
        },
        {
          target: '.loadingHome',
          guard: and([allBlocksSubmitted, not(flashcardsFinished)]),
          actions: [assign({ returnTo: () => null }), resetActiveQuestionIndex],
        },
        {
          target: '.loadingSessionSummary',
          guard: and([allBlocksSubmitted, flashcardsFinished]),
          actions: [assign({ returnTo: () => null }), resetActiveQuestionIndex],
        },
      ],
      START_FLASHCARDS: {
        target: '.loadingFlashcardStudy',
        guard: not(flashcardsFinished),
      },
      FINISH_FLASHCARDS: [
        {
          target: '.loadingHome',
          guard: not(allBlocksSubmitted),
        },
        {
          target: '.loadingSessionSummary',
          guard: and([allBlocksSubmitted, flashcardsFinished]),
        },
      ],
      START_SESSION_GENERIC: [
        {
          target: '.loadingBlockStudy',
          guard: not(allBlocksSubmitted),
        },
        {
          target: '.loadingFlashcardStudy',
          guard: and([allBlocksSubmitted, not(flashcardsFinished)]),
        },
        {
          target: '.loadingSessionSummary',
          guard: and([allBlocksSubmitted, flashcardsFinished]),
        },
      ],
      START_SESSION_SUMMARY: {
        target: '.loadingSessionSummary',
        guard: and([allBlocksSubmitted, flashcardsFinished]),
      },
      END_SESSION: '.loadingHome',
      RETURN_TO_HOME: {
        target: '.loadingHome',
      },
      START_VIDEOS: {
        target: '.loadingVideoStudy',
      },
    },
    states: {
      home: {},
      blockStudy: {},
      blockReview: {},
      blockSelect: {},
      flashcardStudy: {},
      videoStudy: {},
      sessionSummary: {},
      pastBlocks: {},
      loadingBlockStudy: {
        invoke: {
          src: fromPromise(loadBlockStudy),
          onDone: {
            target: 'blockStudy',
          },
          onError: {
            target: 'home',
          },
        },
      },
      loadingBlockReview: {
        invoke: {
          src: fromPromise(({ input }) => loadBlockReview(input.blockId)),
          input: ({ event }) => ({ blockId: event.blockId }),
          onDone: {
            target: 'blockReview',
          },
          onError: {
            target: 'home',
          },
        },
      },
      loadingBlockSelect: {
        invoke: {
          src: fromPromise(loadBlockSelect),
          onDone: {
            target: 'blockSelect',
          },
          onError: {
            target: 'home',
          },
        },
      },
      loadingFlashcardStudy: {
        invoke: {
          src: fromPromise(loadFlashcardStudy),
          onDone: {
            target: 'flashcardStudy',
          },
          onError: {
            target: 'home',
          },
        },
      },
      loadingVideoStudy: {
        invoke: {
          src: fromPromise(loadVideoStudy),
          onDone: {
            target: 'videoStudy',
          },
          onError: {
            target: 'home',
          },
        },
      },
      loadingSessionSummary: {
        invoke: {
          src: fromPromise(loadSessionSummary),
          onDone: {
            target: 'sessionSummary',
          },
          onError: {
            target: 'home',
          },
        },
      },
      loadingPastBlocks: {
        invoke: {
          src: fromPromise(loadPastBlocks),
          onDone: {
            target: 'pastBlocks',
          },
        },
      },
      loadingHome: {
        invoke: {
          src: fromPromise(loadHome),
          onDone: {
            target: 'home',
          },
        },
      },
    },
  },
  {
    /** Guards can reference functions directly in v5 */
    guards: {
      allBlocksSubmitted,
      flashcardsFinished,
      isOneBlockSession,
      isBlockSubmitted,
      cameFromPastBlocks,
    },
  }
);
